import Player from '@vimeo/player';
import { useEffect, useState } from 'react';
import { getFlattenContentPages } from '@/utils';

import {
  IUseRequiredMediaCompletion,
  IUseRequiredMediaCompletionParams,
} from './useRequiredMediaCompletion.interfaces';

// Patched once per page load. Intercepts completeAudio and trackAudio mutations
// fired by the ekardo audio player and re-dispatches them as DOM events.
let fetchPatched = false;
const patchFetch = () => {
  if (fetchPatched) return;
  fetchPatched = true;
  const original = window.fetch;
  window.fetch = function (...args: Parameters<typeof fetch>) {
    try {
      const body = typeof args[1]?.body === 'string'
        ? (JSON.parse(args[1].body) as { operationName?: string; variables?: { contentSnippetAudioId?: number; positionMs?: number } })
        : null;
      if (body?.operationName === 'completeAudio' && body.variables?.contentSnippetAudioId != null) {
        document.dispatchEvent(
          new CustomEvent('audioComplete', { detail: { contentSnippetAudioId: body.variables.contentSnippetAudioId } })
        );
      }
      if (body?.operationName === 'trackAudio' && body.variables?.contentSnippetAudioId != null && body.variables.positionMs != null) {
        document.dispatchEvent(
          new CustomEvent('audioTrackUpdate', { detail: { contentSnippetAudioId: body.variables.contentSnippetAudioId, positionMs: body.variables.positionMs } })
        );
      }
    } catch {}
    return original.apply(this, args);
  };
};

const useRequiredMediaCompletion = ({
  contentPage,
  contentPages,
  hasContentPageData,
}: IUseRequiredMediaCompletionParams): IUseRequiredMediaCompletion => {
  const [isNextUnlocked, setIsNextUnlocked] = useState(true);
  const [blockedBy, setBlockedBy] = useState(false);

  useEffect(() => {
    if (!contentPage || !hasContentPageData) return;

    const snippets = contentPage.sectionGroups.flatMap(sg =>
      sg.contentSections.flatMap(cs =>
        cs.sectionContainers.flatMap(sc => sc.snippets)
      )
    );

    const requiredAudio = snippets.find(s => s.__typename === 'ContentSnippetAudioType' && s.mustBeCompleted);
    const requiredEmbed = snippets.find(s => s.__typename === 'ContentSnippetEmbedType' && s.mustBeCompleted);

    if (!requiredAudio && !requiredEmbed) {
      setIsNextUnlocked(true);
      setBlockedBy(false);
      return;
    }

    // If this page was already completed in a previous session the required
    // media was already consumed — unlock without requiring it again.
    const flatPages = getFlattenContentPages(contentPages);
    const currentPageRecord = flatPages.find(p => p.id === contentPage.id);
    if (currentPageRecord?.isCompleted) {
      setIsNextUnlocked(true);
      setBlockedBy(false);
      return;
    }

    setIsNextUnlocked(false);
    setBlockedBy(true);

    const required = new Set<string>([
      ...(requiredAudio ? ['audio'] : []),
      ...(requiredEmbed ? ['embed'] : []),
    ]);
    const unlocked = new Set<string>();
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    const checkAnyUnlocked = () => {
      if (Array.from(required).some(k => unlocked.has(k))) {
        setIsNextUnlocked(true);
      }
    };

    if (requiredAudio) {
      patchFetch();

      const { id: audioSnippetId, duration: snippetDuration } = requiredAudio;

      const unlockAudio = () => {
        if (cancelled) return;
        unlocked.add('audio');
        checkAnyUnlocked();
      };

      // Unlock when the player fires completeAudio (natural end)
      const handleAudioComplete = (e: Event) => {
        const { contentSnippetAudioId } = (e as CustomEvent<{ contentSnippetAudioId: number }>).detail;
        if (contentSnippetAudioId === audioSnippetId) unlockAudio();
      };
      document.addEventListener('audioComplete', handleAudioComplete);
      cleanups.push(() => document.removeEventListener('audioComplete', handleAudioComplete));

      // Also unlock when trackAudio reports >= 90% progress
      const handleTrackUpdate = (e: Event) => {
        if (!snippetDuration) return;
        const { contentSnippetAudioId, positionMs } = (e as CustomEvent<{ contentSnippetAudioId: number; positionMs: number }>).detail;
        if (contentSnippetAudioId === audioSnippetId && positionMs / 1000 / snippetDuration >= 0.9) unlockAudio();
      };
      document.addEventListener('audioTrackUpdate', handleTrackUpdate);
      cleanups.push(() => document.removeEventListener('audioTrackUpdate', handleTrackUpdate));
    }

    if (requiredEmbed) {
      const iframe = document.querySelector('iframe[src*="player.vimeo.com"]') as HTMLIFrameElement | null;
      if (!iframe) {
        unlocked.add('embed');
      } else {
        const player = new Player(iframe);

        player.on('ended', () => {
          if (cancelled) return;
          unlocked.add('embed');
          checkAnyUnlocked();
        });

        player.getDuration().then(duration => {
          if (cancelled) return;
          const resumeSeconds = (requiredEmbed.positionMs ?? 0) / 1000;
          if (duration > 0 && resumeSeconds / duration >= 0.999) {
            unlocked.add('embed');
            checkAnyUnlocked();
          }
        }).catch(() => {});

        cleanups.push(() => { try { player.off('ended'); } catch {} });
      }
    }

    checkAnyUnlocked();

    return () => {
      cancelled = true;
      cleanups.forEach(c => c());
    };
  }, [hasContentPageData, contentPage, contentPages]);

  return { blockedBy, isNextUnlocked };
};

export { useRequiredMediaCompletion };
